import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  Building2, Users, Plus, TrendingUp,
  MapPin, Phone, ArrowRight, Upload, ImageIcon, Loader2,
  UserPlus, Clock, ChevronDown,
  ToggleLeft, ToggleRight, CreditCard, CalendarDays, Settings2, Eye,
  DollarSign, AlertTriangle, CheckCircle, XCircle, Percent, UserCheck,
  ArrowUpRight, ArrowDownRight, CircleAlert, Sparkles, KeyRound, Mail, User
} from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function LeadsSection({ leads, loading, statusConfig, onUpdateStatus }) {
  const [filter, setFilter] = useState("todos");
  const [openDropdown, setOpenDropdown] = useState(null);

  const filtered = filter === "todos" ? leads : leads.filter(l => l.status === filter);

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return <p className="text-slate-400 text-center py-8">Cargando leads...</p>;

  return (
    <div className="space-y-4" data-testid="leads-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Clientes Potenciales</h2>
          <p className="text-xs text-slate-400 mt-0.5">{leads.length} solicitudes de demo recibidas</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[
            { key: "todos", label: "Todos" },
            { key: "nuevo", label: "Nuevos" },
            { key: "contactado", label: "Contactados" },
            { key: "interesado", label: "Interesados" },
            { key: "convertido", label: "Convertidos" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${filter === f.key ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-10 text-center">
            <UserPlus className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400 font-medium">
              {filter === "todos" ? "Aún no hay solicitudes de demo" : "Sin resultados para este filtro"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => {
            const sc = statusConfig[lead.status] || statusConfig.nuevo;
            return (
              <Card key={lead.id} className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all" data-testid={`lead-card-${lead.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-base flex-shrink-0">
                      {lead.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900 text-sm">{lead.name}</h3>
                        <Badge className={`rounded-full border text-[10px] font-semibold ${sc.color}`}>{sc.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                        <Building2 className="w-3 h-3 text-slate-400" /> {lead.facility_name}
                      </p>
                      <div className="flex items-center gap-4 mt-1.5">
                        <a href={`tel:${lead.phone}`} className="text-xs text-violet-600 font-medium flex items-center gap-1 hover:underline" data-testid={`lead-phone-${lead.id}`}>
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </a>
                        <span className="text-[10px] text-slate-300 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {formatDate(lead.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="relative flex-shrink-0">
                      <button
                        onClick={() => setOpenDropdown(openDropdown === lead.id ? null : lead.id)}
                        className="text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg transition-all flex items-center gap-1"
                        data-testid={`lead-status-btn-${lead.id}`}
                      >
                        Cambiar estado <ChevronDown className="w-3 h-3" />
                      </button>
                      {openDropdown === lead.id && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-20 min-w-[160px]" data-testid={`lead-status-dropdown-${lead.id}`}>
                          {Object.entries(statusConfig).map(([key, cfg]) => (
                            <button
                              key={key}
                              onClick={() => { onUpdateStatus(lead.id, key); setOpenDropdown(null); }}
                              className={`w-full text-left text-xs font-medium px-4 py-2 hover:bg-slate-50 transition-colors ${lead.status === key ? "text-violet-600" : "text-slate-600"}`}
                              data-testid={`lead-set-status-${key}`}
                            >
                              {cfg.label} {lead.status === key && <span className="text-violet-400 ml-1">•</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FEATURE_LIST = [
  { key: "residentes", label: "Residentes" },
  { key: "ronda_medicacion", label: "Ronda Medicación" },
  { key: "stock_medicamentos", label: "Stock Medicamentos" },
  { key: "medicos", label: "Médicos" },
  { key: "turnos_personal", label: "Turnos Personal" },
  { key: "novedades", label: "Novedades" },
  { key: "incidentes", label: "Incidentes" },
  { key: "portal_familiar", label: "Portal Familiar" },
  { key: "mensajes", label: "Mensajes" },
  { key: "cuidado_diario", label: "Cuidado Diario" },
  { key: "historia_clinica", label: "Historia Clínica" },
];

function TenantManagement({ tenants, onSelectTenant, selectedTenant, editBilling, setEditBilling, saveBilling, updateTenantFeatures }) {
  const paymentConfig = {
    pendiente: { label: "Pendiente", color: "bg-amber-50 text-amber-700 border-amber-100" },
    al_dia: { label: "Al día", color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    atrasado: { label: "Atrasado", color: "bg-rose-50 text-rose-600 border-rose-100" },
    bonificado: { label: "Bonificado", color: "bg-violet-50 text-violet-700 border-violet-100" },
  };

  if (selectedTenant) {
    const t = selectedTenant;
    const ps = paymentConfig[t.payment_status] || paymentConfig.pendiente;
    const feats = t.features || {};
    return (
      <div className="space-y-4" data-testid="tenant-detail">
        <button onClick={() => onSelectTenant(null)} className="text-xs font-semibold text-violet-600 hover:underline flex items-center gap-1" data-testid="back-to-tenants">
          &larr; Volver a Geriátricos
        </button>
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-14 h-14 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-xl flex-shrink-0">{t.name?.charAt(0)}</div>
              <div className="flex-1">
                <h2 className="font-bold text-slate-900 text-lg">{t.name}</h2>
                {t.address && <p className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {t.address}</p>}
                {t.phone && <p className="text-xs text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3" /> {t.phone}</p>}
                <div className="flex gap-2 mt-2">
                  <Badge className="rounded-full bg-slate-100 text-slate-600 border-0 text-[10px] font-semibold"><Users className="w-3 h-3 mr-1" /> {t.user_count ?? 0} usuarios</Badge>
                  <Badge className="rounded-full bg-emerald-50 text-emerald-700 border-0 text-[10px] font-semibold">{t.resident_count ?? 0} residentes</Badge>
                  <Badge className={`rounded-full border text-[10px] font-semibold ${ps.color}`}>{ps.label}</Badge>
                </div>
              </div>
            </div>

            {/* Billing */}
            <div className="border-t border-slate-100 pt-4 mb-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Facturación</p>
                {!editBilling && (
                  <button onClick={() => setEditBilling({ ...t })} className="text-xs font-semibold text-violet-600 hover:underline" data-testid="edit-billing-btn">Editar</button>
                )}
              </div>
              {editBilling ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-[10px] text-slate-400 uppercase font-semibold">Plan</Label>
                      <Select value={editBilling.plan || "basico"} onValueChange={v => setEditBilling({...editBilling, plan: v})}><SelectTrigger className="rounded-xl h-9 bg-slate-50 border-slate-200 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="basico">Básico</SelectItem><SelectItem value="profesional">Profesional</SelectItem><SelectItem value="premium">Premium</SelectItem></SelectContent></Select>
                    </div>
                    <div className="space-y-1"><Label className="text-[10px] text-slate-400 uppercase font-semibold">Precio/mes</Label><Input type="number" value={editBilling.monthly_price || 0} onChange={e => setEditBilling({...editBilling, monthly_price: e.target.value})} className="rounded-xl h-9 bg-slate-50 border-slate-200 text-sm" data-testid="billing-price" /></div>
                    <div className="space-y-1"><Label className="text-[10px] text-slate-400 uppercase font-semibold">Estado Pago</Label>
                      <Select value={editBilling.payment_status || "pendiente"} onValueChange={v => setEditBilling({...editBilling, payment_status: v})}><SelectTrigger className="rounded-xl h-9 bg-slate-50 border-slate-200 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pendiente">Pendiente</SelectItem><SelectItem value="al_dia">Al día</SelectItem><SelectItem value="atrasado">Atrasado</SelectItem><SelectItem value="bonificado">Bonificado</SelectItem></SelectContent></Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-[10px] text-slate-400 uppercase font-semibold">Fecha Ingreso</Label><Input type="date" value={editBilling.subscription_start || ""} onChange={e => setEditBilling({...editBilling, subscription_start: e.target.value})} className="rounded-xl h-9 bg-slate-50 border-slate-200 text-sm" /></div>
                    <div className="space-y-1"><Label className="text-[10px] text-slate-400 uppercase font-semibold">Vencimiento</Label><Input type="date" value={editBilling.subscription_end || ""} onChange={e => setEditBilling({...editBilling, subscription_end: e.target.value})} className="rounded-xl h-9 bg-slate-50 border-slate-200 text-sm" /></div>
                    <div className="space-y-1"><Label className="text-[10px] text-slate-400 uppercase font-semibold">Máx Residentes</Label><Input type="number" value={editBilling.max_residents || 50} onChange={e => setEditBilling({...editBilling, max_residents: e.target.value})} className="rounded-xl h-9 bg-slate-50 border-slate-200 text-sm" /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-[10px] text-slate-400 uppercase font-semibold">Notas</Label><Input value={editBilling.notes || ""} onChange={e => setEditBilling({...editBilling, notes: e.target.value})} className="rounded-xl h-9 bg-slate-50 border-slate-200 text-sm" placeholder="Observaciones sobre este cliente..." /></div>
                  <div className="flex gap-2">
                    <Button onClick={saveBilling} className="rounded-xl h-9 px-5 bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid="save-billing-btn">Guardar</Button>
                    <Button variant="outline" onClick={() => setEditBilling(null)} className="rounded-xl h-9 px-5 text-xs font-semibold border-slate-200">Cancelar</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Plan", value: (t.plan || "basico").charAt(0).toUpperCase() + (t.plan || "basico").slice(1) },
                    { label: "Precio/mes", value: t.monthly_price ? `$${Number(t.monthly_price).toLocaleString("es-AR")}` : "$0" },
                    { label: "Ingreso", value: t.subscription_start || t.created_at?.substring(0,10) || "—" },
                    { label: "Vencimiento", value: t.subscription_end || "Sin definir" },
                  ].map(item => (
                    <div key={item.label} className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">{item.label}</p>
                      <p className="text-sm font-semibold text-slate-700 mt-1">{item.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Features */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Módulos habilitados</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {FEATURE_LIST.map(f => {
                  const enabled = feats[f.key] !== false;
                  return (
                    <button
                      key={f.key}
                      onClick={() => updateTenantFeatures(t.id, { ...feats, [f.key]: !enabled })}
                      className={`flex items-center gap-2 rounded-xl px-3 py-2.5 border text-left transition-all text-xs font-medium ${
                        enabled ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-400"
                      }`}
                      data-testid={`tenant-feature-${f.key}`}
                    >
                      {enabled ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4" />}
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="tenants-list">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Geriátricos ({tenants.length})</h2>
      </div>
      {tenants.map(t => {
        const ps = paymentConfig[t.payment_status] || paymentConfig.pendiente;
        const enabledCount = FEATURE_LIST.filter(f => (t.features || {})[f.key] !== false).length;
        return (
          <Card key={t.id} className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer"
            onClick={() => onSelectTenant(t)} data-testid={`tenant-row-${t.id}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-lg flex-shrink-0">{t.name?.charAt(0)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900 text-sm">{t.name}</h3>
                    <Badge className={`rounded-full border text-[10px] font-semibold ${ps.color}`}>{ps.label}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-xs text-slate-400"><Users className="w-3 h-3 inline mr-1" />{t.resident_count ?? 0} residentes</span>
                    <span className="text-xs text-slate-400"><CreditCard className="w-3 h-3 inline mr-1" />{t.monthly_price ? `$${Number(t.monthly_price).toLocaleString("es-AR")}/mes` : "Sin precio"}</span>
                    <span className="text-xs text-slate-400"><Settings2 className="w-3 h-3 inline mr-1" />{enabledCount}/{FEATURE_LIST.length} módulos</span>
                    {t.subscription_end && <span className="text-xs text-slate-400"><CalendarDays className="w-3 h-3 inline mr-1" />Vence: {t.subscription_end}</span>}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

const PAYMENT_COLORS = { al_dia: "#10b981", pendiente: "#f59e0b", atrasado: "#ef4444", bonificado: "#8b5cf6" };
const PAYMENT_LABELS = { al_dia: "Al día", pendiente: "Pendiente", atrasado: "Atrasado", bonificado: "Bonificado" };
const PLAN_COLORS = { basico: "#94a3b8", profesional: "#8b5cf6", premium: "#f59e0b" };

function ProfileSection({ currentUser, onProfileUpdated }) {
  const [email, setEmail] = useState(currentUser?.email || "");
  const [name, setName] = useState(currentUser?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (newPassword && newPassword.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (email !== currentUser.email) payload.email = email;
      if (name !== currentUser.name) payload.name = name;
      if (newPassword) {
        payload.current_password = currentPassword;
        payload.new_password = newPassword;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("No hay cambios para guardar");
        setSaving(false);
        return;
      }
      const res = await api.put("/auth/profile", payload);
      toast.success("Perfil actualizado correctamente");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      if (onProfileUpdated) onProfileUpdated(res.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar perfil");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-lg space-y-5" data-testid="profile-section">
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <User className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Datos de cuenta</h3>
              <p className="text-[10px] text-slate-400">Modificá tu email o nombre</p>
            </div>
          </div>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-medium text-xs text-slate-600">Nombre</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl h-10 bg-slate-50 border-slate-200 text-sm" data-testid="profile-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-xs text-slate-600">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl h-10 bg-slate-50 border-slate-200 text-sm" required data-testid="profile-email-input" />
            </div>

            <Separator className="my-2" />

            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Cambiar contraseña</h3>
                <p className="text-[10px] text-slate-400">Dejá en blanco si no querés cambiarla</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-xs text-slate-600">Contraseña actual</Label>
              <div className="relative">
                <Input type={showCurrentPass ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="rounded-xl h-10 bg-slate-50 border-slate-200 text-sm pr-10" placeholder="Ingresá tu contraseña actual" data-testid="profile-current-password" />
                <button type="button" onClick={() => setShowCurrentPass(!showCurrentPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrentPass ? <XCircle className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-medium text-xs text-slate-600">Nueva contraseña</Label>
                <div className="relative">
                  <Input type={showNewPass ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="rounded-xl h-10 bg-slate-50 border-slate-200 text-sm pr-10" placeholder="Nueva contraseña" data-testid="profile-new-password" />
                  <button type="button" onClick={() => setShowNewPass(!showNewPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNewPass ? <XCircle className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-xs text-slate-600">Confirmar contraseña</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="rounded-xl h-10 bg-slate-50 border-slate-200 text-sm" placeholder="Repetí la nueva" data-testid="profile-confirm-password" />
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full sm:w-auto rounded-xl h-10 px-8 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all text-sm" data-testid="profile-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function BusinessDashboard({ metrics, tenants, leads, fmt, platformLogoUrl, handlePlatformLogoUpload, uploadingLogo, fileInputRef, onNavigateToTenants, onNavigateToLeads }) {
  if (!metrics) return null;

  const paymentData = Object.entries(metrics.payment_distribution || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: PAYMENT_LABELS[k] || k, value: v, color: PAYMENT_COLORS[k] || "#94a3b8" }));

  const planData = Object.entries(metrics.plan_distribution || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), count: v, amount: metrics.plan_amounts?.[k] || 0, color: PLAN_COLORS[k] || "#94a3b8" }));

  const overdue = metrics.overdue || [];
  const expiring = metrics.expiring_soon || [];
  const newLeads = leads.filter(l => l.status === "nuevo").length;

  const alertCount = overdue.length + expiring.length + newLeads;

  return (
    <div className="space-y-5" data-testid="business-dashboard">
      {/* KPI Row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] animate-fade-in-up">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" strokeWidth={1.8} /></div>
              <Badge className="rounded-full bg-emerald-50 text-emerald-700 border-0 text-[9px] sm:text-[10px] font-bold">MRR</Badge>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-slate-900 font-[Manrope]" data-testid="kpi-mrr">{fmt(metrics.mrr)}</p>
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium mt-0.5">Facturación mensual</p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] animate-fade-in-up">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-violet-50 flex items-center justify-center"><Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600" strokeWidth={1.8} /></div>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-slate-900 font-[Manrope]" data-testid="kpi-tenants">{metrics.total_tenants}</p>
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium mt-0.5">Geriátricos activos</p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] animate-fade-in-up">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" strokeWidth={1.8} /></div>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-slate-900 font-[Manrope]" data-testid="kpi-residents">{metrics.total_residents}</p>
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium mt-0.5">Residentes totales</p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] animate-fade-in-up">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${metrics.collection_rate >= 70 ? "bg-emerald-50" : "bg-amber-50"}`}>
                <Percent className={`w-4 h-4 sm:w-5 sm:h-5 ${metrics.collection_rate >= 70 ? "text-emerald-600" : "text-amber-600"}`} strokeWidth={1.8} />
              </div>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-slate-900 font-[Manrope]" data-testid="kpi-collection">{metrics.collection_rate}%</p>
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium mt-0.5">Tasa de cobranza</p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section */}
      {alertCount > 0 && (
        <Card className="rounded-2xl border-l-4 border-l-amber-400 border-slate-100 shadow-sm" data-testid="alerts-section">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <CircleAlert className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-slate-900">Atención requerida</h3>
              <Badge className="rounded-full bg-amber-50 text-amber-700 border-0 text-[10px] font-bold">{alertCount}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              {overdue.length > 0 && (
                <button onClick={onNavigateToTenants} className="flex items-start gap-3 bg-rose-50 rounded-xl p-3.5 text-left hover:bg-rose-100 transition-colors" data-testid="alert-overdue">
                  <XCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-rose-700">{overdue.length} pago{overdue.length > 1 ? "s" : ""} atrasado{overdue.length > 1 ? "s" : ""}</p>
                    <p className="text-[10px] text-rose-500 mt-0.5">{overdue.map(o => o.name).join(", ")}</p>
                  </div>
                </button>
              )}
              {expiring.length > 0 && (
                <button onClick={onNavigateToTenants} className="flex items-start gap-3 bg-amber-50 rounded-xl p-3.5 text-left hover:bg-amber-100 transition-colors" data-testid="alert-expiring">
                  <CalendarDays className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-amber-700">{expiring.length} vencimiento{expiring.length > 1 ? "s" : ""} próximo{expiring.length > 1 ? "s" : ""}</p>
                    <p className="text-[10px] text-amber-500 mt-0.5">{expiring.map(e => `${e.name} (${e.subscription_end})`).join(", ")}</p>
                  </div>
                </button>
              )}
              {newLeads > 0 && (
                <button onClick={onNavigateToLeads} className="flex items-start gap-3 bg-blue-50 rounded-xl p-3.5 text-left hover:bg-blue-100 transition-colors" data-testid="alert-leads">
                  <Sparkles className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-blue-700">{newLeads} lead{newLeads > 1 ? "s" : ""} nuevo{newLeads > 1 ? "s" : ""}</p>
                    <p className="text-[10px] text-blue-500 mt-0.5">Solicitudes de demo sin contactar</p>
                  </div>
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Payment Status Chart */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Estado de Pagos</h3>
            {paymentData.length > 0 ? (
              <div className="flex items-center gap-6">
                <div className="w-36 h-36 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={paymentData} cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={3} dataKey="value" strokeWidth={0}>
                        {paymentData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2.5 flex-1">
                  {paymentData.map((d) => (
                    <div key={d.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className="text-xs font-medium text-slate-600">{d.name}</span>
                      </div>
                      <span className="text-xs font-bold text-slate-900">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-8">Sin datos de pago aún</p>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Plan Chart */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Facturación por Plan</h3>
            {planData.length > 0 ? (
              <ResponsiveContainer width="100%" height={144}>
                <BarChart data={planData} barSize={36}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v) => [`$${Number(v).toLocaleString("es-AR")}`, "Facturación"]}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                  />
                  <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                    {planData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-slate-400 text-center py-8">Sin datos de planes aún</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tenant Health */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Salud por Geriátrico</h3>
            <button onClick={onNavigateToTenants} className="text-xs font-semibold text-violet-600 hover:underline flex items-center gap-1" data-testid="go-to-tenants">
              Ver todos <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {/* Mobile: card list */}
          <div className="space-y-2.5 sm:hidden" data-testid="tenant-health-mobile">
            {(metrics.tenants || []).map((t) => {
              const psColor = {
                al_dia: "bg-emerald-50 text-emerald-700 border-emerald-100",
                pendiente: "bg-amber-50 text-amber-700 border-amber-100",
                atrasado: "bg-rose-50 text-rose-600 border-rose-100",
                bonificado: "bg-violet-50 text-violet-700 border-violet-100",
              }[t.payment_status] || "bg-slate-50 text-slate-500 border-slate-100";
              return (
                <div key={t.id} className="bg-slate-50 rounded-xl p-3.5">
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-xs flex-shrink-0">{t.name?.charAt(0)}</div>
                    <span className="text-xs font-semibold text-slate-800 flex-1 truncate">{t.name}</span>
                    <Badge className={`rounded-full border text-[10px] font-semibold ${psColor}`}>{PAYMENT_LABELS[t.payment_status] || "Pendiente"}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-[10px] text-slate-400 uppercase">Plan</p><p className="text-xs font-semibold text-slate-700 capitalize">{t.plan || "—"}</p></div>
                    <div><p className="text-[10px] text-slate-400 uppercase">Precio</p><p className="text-xs font-semibold text-slate-700">{fmt(t.monthly_price)}</p></div>
                    <div><p className="text-[10px] text-slate-400 uppercase">Residentes</p><p className="text-xs font-semibold text-slate-700">{t.residents}</p></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left" data-testid="tenant-health-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider pb-2.5 pr-4">Geriátrico</th>
                  <th className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider pb-2.5 pr-4">Plan</th>
                  <th className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider pb-2.5 pr-4 text-right">Precio/mes</th>
                  <th className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider pb-2.5 pr-4 text-center">Residentes</th>
                  <th className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider pb-2.5 pr-4 text-center">Usuarios</th>
                  <th className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider pb-2.5">Estado Pago</th>
                  <th className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider pb-2.5 text-right">Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                {(metrics.tenants || []).map((t) => {
                  const psColor = {
                    al_dia: "bg-emerald-50 text-emerald-700 border-emerald-100",
                    pendiente: "bg-amber-50 text-amber-700 border-amber-100",
                    atrasado: "bg-rose-50 text-rose-600 border-rose-100",
                    bonificado: "bg-violet-50 text-violet-700 border-violet-100",
                  }[t.payment_status] || "bg-slate-50 text-slate-500 border-slate-100";
                  return (
                    <tr key={t.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-xs flex-shrink-0">{t.name?.charAt(0)}</div>
                          <span className="text-xs font-medium text-slate-800 truncate max-w-[180px]">{t.name}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4"><span className="text-xs font-medium text-slate-600 capitalize">{t.plan || "—"}</span></td>
                      <td className="py-3 pr-4 text-right"><span className="text-xs font-semibold text-slate-800">{fmt(t.monthly_price)}</span></td>
                      <td className="py-3 pr-4 text-center"><span className="text-xs font-medium text-slate-600">{t.residents}</span></td>
                      <td className="py-3 pr-4 text-center"><span className="text-xs font-medium text-slate-600">{t.users}</span></td>
                      <td className="py-3 pr-4"><Badge className={`rounded-full border text-[10px] font-semibold ${psColor}`}>{PAYMENT_LABELS[t.payment_status] || "Pendiente"}</Badge></td>
                      <td className="py-3 text-right"><span className="text-[11px] text-slate-400">{t.subscription_end || "—"}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Platform Logo - compact */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg border border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0" data-testid="platform-logo-preview">
              {platformLogoUrl && (
                <img src={platformLogoUrl} alt="Logo" className="w-full h-full object-contain"
                  onError={(e) => { e.target.style.display = "none"; }}
                  onLoad={(e) => { e.target.style.display = "block"; }}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-600">Logo de Gerion</p>
              <p className="text-[10px] text-slate-400">Login, sidebar y reportes</p>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePlatformLogoUpload} data-testid="platform-logo-file-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo} variant="outline"
              className="rounded-xl h-8 px-4 text-xs font-semibold border-slate-200"
              data-testid="upload-platform-logo-btn">
              {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Cambiar</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function SuperAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", address: "", phone: "", license_number: "",
    plan: "basico", monthly_price: 0, max_residents: 50,
    subscription_start: new Date().toISOString().split("T")[0], subscription_end: "",
    features: {
      residentes: true, ronda_medicacion: true, stock_medicamentos: true,
      medicos: true, turnos_personal: true, novedades: true,
      incidentes: true, portal_familiar: true, mensajes: true,
      cuidado_diario: true, historia_clinica: true,
    }
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [platformLogoUrl, setPlatformLogoUrl] = useState(null);
  const [logoKey, setLogoKey] = useState(0);
  const fileInputRef = useRef(null);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [editBilling, setEditBilling] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, mRes] = await Promise.all([
        api.get("/tenants"),
        api.get("/global/metrics")
      ]);
      setTenants(tRes.data);
      setMetrics(mRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch { /* ignore */ }
    setLeadsLoading(false);
  }, []);

  useEffect(() => { fetchData(); fetchLeads(); }, [fetchData, fetchLeads]);

  useEffect(() => {
    setPlatformLogoUrl(`${API_URL}/api/platform/logo?t=${Date.now()}`);
  }, [logoKey]);

  if (user?.role !== "super_admin") {
    return (
      <div className="text-center py-16">
        <p className="text-rose-600 font-semibold">Acceso denegado. Solo Super Admin.</p>
      </div>
    );
  }

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.put(`/leads/${leadId}`, { status });
      toast.success("Estado actualizado");
      fetchLeads();
    } catch { toast.error("Error al actualizar"); }
  };

  const saveBilling = async () => {
    if (!editBilling) return;
    try {
      await api.put(`/tenants/${editBilling.id}`, {
        plan: editBilling.plan,
        monthly_price: parseFloat(editBilling.monthly_price) || 0,
        payment_status: editBilling.payment_status,
        subscription_start: editBilling.subscription_start,
        subscription_end: editBilling.subscription_end,
        max_residents: parseInt(editBilling.max_residents) || 50,
        notes: editBilling.notes,
      });
      toast.success("Facturación actualizada");
      setEditBilling(null);
      fetchData();
    } catch { toast.error("Error al guardar"); }
  };

  const updateTenantFeatures = async (tenantId, features) => {
    try {
      await api.put(`/tenants/${tenantId}`, { features });
      toast.success("Features actualizados");
      fetchData();
    } catch { toast.error("Error al actualizar"); }
  };

  const handlePlatformLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Solo se permiten archivos de imagen"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("El archivo no puede superar 2MB"); return; }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post("/platform/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo de Gerion actualizado");
      setLogoKey(prev => prev + 1);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir el logo"); }
    finally { setUploadingLogo(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/tenants", form);
      toast.success(`Geriátrico "${form.name}" creado`);
      setDialogOpen(false);
      setForm({ name: "", address: "", phone: "", license_number: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <p className="text-slate-400 text-center py-12">Cargando panel global...</p>;

  const fmt = (n) => n != null ? `$${Number(n).toLocaleString("es-AR")}` : "$0";
  const newLeadsCount = leads.filter(l => l.status === "nuevo").length;

  const leadStatusConfig = {
    nuevo: { label: "Nuevo", color: "bg-blue-50 text-blue-700 border-blue-100" },
    contactado: { label: "Contactado", color: "bg-amber-50 text-amber-700 border-amber-100" },
    interesado: { label: "Interesado", color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    no_interesado: { label: "No interesado", color: "bg-slate-100 text-slate-500 border-slate-200" },
    convertido: { label: "Convertido", color: "bg-violet-50 text-violet-700 border-violet-100" },
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in-up" data-testid="super-admin-page">
      <div className="flex flex-col gap-3">
        <div>
          <Badge className="rounded-full bg-violet-100 text-violet-700 border-0 text-[10px] font-bold uppercase tracking-wider mb-1">Super Admin</Badge>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Panel Global</h1>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl px-5 h-10 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold w-full sm:w-auto" data-testid="add-tenant-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Geriátrico
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Nuevo Geriátrico</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">Crear una nueva residencia y configurar sus features</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTenant} className="space-y-4 mt-2">
              <div className="space-y-2"><Label className="font-medium text-sm text-slate-700">Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="tenant-name-input" /></div>
              <div className="space-y-2"><Label className="font-medium text-sm text-slate-700">Dirección</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="tenant-address-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label className="font-medium text-sm text-slate-700">Teléfono</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="tenant-phone-input" /></div>
                <div className="space-y-2"><Label className="font-medium text-sm text-slate-700">Habilitación</Label><Input value={form.license_number} onChange={e => setForm({...form, license_number: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="tenant-license-input" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label className="font-medium text-sm text-slate-700">Plan</Label>
                  <Select value={form.plan} onValueChange={v => setForm({...form, plan: v})}><SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="basico">Básico</SelectItem><SelectItem value="profesional">Profesional</SelectItem><SelectItem value="premium">Premium</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-2"><Label className="font-medium text-sm text-slate-700">Precio/mes</Label><Input type="number" value={form.monthly_price} onChange={e => setForm({...form, monthly_price: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="tenant-price-input" /></div>
                <div className="space-y-2"><Label className="font-medium text-sm text-slate-700">Máx residentes</Label><Input type="number" value={form.max_residents} onChange={e => setForm({...form, max_residents: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="tenant-maxres-input" /></div>
              </div>

              {/* Feature toggles */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Módulos habilitados</p>
                <div className="grid grid-cols-2 gap-2">
                  {FEATURE_LIST.map(f => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setForm({...form, features: {...form.features, [f.key]: !form.features[f.key]}})}
                      className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 border text-left transition-all text-xs font-medium ${
                        form.features[f.key]
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                          : "bg-slate-50 border-slate-200 text-slate-400"
                      }`}
                      data-testid={`feature-toggle-${f.key}`}
                    >
                      {form.features[f.key] ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4" />}
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-tenant-btn">Crear Geriátrico</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto" data-testid="super-admin-tabs">
        <button
          onClick={() => { setActiveTab("overview"); setSelectedTenant(null); }}
          className={`flex-1 min-w-[90px] text-xs sm:text-sm font-semibold py-2.5 rounded-lg transition-all whitespace-nowrap ${activeTab === "overview" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-overview"
        >
          Panel General
        </button>
        <button
          onClick={() => setActiveTab("tenants")}
          className={`flex-1 min-w-[90px] text-xs sm:text-sm font-semibold py-2.5 rounded-lg transition-all whitespace-nowrap ${activeTab === "tenants" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-tenants"
        >
          Geriátricos
        </button>
        <button
          onClick={() => setActiveTab("leads")}
          className={`flex-1 min-w-[90px] text-xs sm:text-sm font-semibold py-2.5 rounded-lg transition-all relative whitespace-nowrap ${activeTab === "leads" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-leads"
        >
          Leads
          {newLeadsCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {newLeadsCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("profile")}
          className={`flex-1 min-w-[90px] text-xs sm:text-sm font-semibold py-2.5 rounded-lg transition-all whitespace-nowrap ${activeTab === "profile" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-profile"
        >
          Mi Cuenta
        </button>
      </div>

      {activeTab === "overview" && (
        <BusinessDashboard
          metrics={metrics}
          tenants={tenants}
          leads={leads}
          fmt={fmt}
          platformLogoUrl={platformLogoUrl}
          handlePlatformLogoUpload={handlePlatformLogoUpload}
          uploadingLogo={uploadingLogo}
          fileInputRef={fileInputRef}
          onNavigateToTenants={() => setActiveTab("tenants")}
          onNavigateToLeads={() => setActiveTab("leads")}
        />
      )}

      {activeTab === "tenants" && (
        <TenantManagement
          tenants={tenants}
          onSelectTenant={setSelectedTenant}
          selectedTenant={selectedTenant}
          editBilling={editBilling}
          setEditBilling={setEditBilling}
          saveBilling={saveBilling}
          updateTenantFeatures={updateTenantFeatures}
        />
      )}

      {activeTab === "leads" && (
        <LeadsSection
          leads={leads}
          loading={leadsLoading}
          statusConfig={leadStatusConfig}
          onUpdateStatus={updateLeadStatus}
        />
      )}

      {activeTab === "profile" && (
        <ProfileSection
          currentUser={user}
          onProfileUpdated={(updatedUser) => {
            toast.success("Perfil actualizado. Si cambiaste el email, usá el nuevo para ingresar.");
          }}
        />
      )}
    </div>
  );
}
